import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  collection, addDoc, onSnapshot, query,
  orderBy, serverTimestamp, deleteDoc, doc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiStar, FiSend, FiTrash2, FiMessageSquare, FiLogIn } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(n => (
      <motion.button key={n} type="button"
        whileHover={{ scale: 1.25 }} whileTap={{ scale: 0.9 }}
        onClick={() => onChange(n)}
        style={{ color: n <= value ? 'var(--gold)' : 'var(--border)', background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
        <FiStar size={20} fill={n <= value ? 'var(--gold)' : 'none'} />
      </motion.button>
    ))}
  </div>
);

const timeAgo = (ts) => {
  if (!ts?.toMillis) return '';
  const s = Math.floor((Date.now() - ts.toMillis()) / 1000);
  if (s < 60)    return 'just now';
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const ReviewSection = ({ movieId }) => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [reviews,    setReviews]    = useState([]);
  const [text,       setText]       = useState('');
  const [stars,      setStars]      = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');

  const colRef = useCallback(
    () => collection(db, 'movies', String(movieId), 'reviews'),
    [movieId]
  );

  useEffect(() => {
    const q = query(colRef(), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => setReviews(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [colRef]);

  const avgRating = reviews.length
    ? (reviews.reduce((a, r) => a + (r.stars || 0), 0) / reviews.length).toFixed(1)
    : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) { setError('Write something first.'); return; }
    if (!stars)       { setError('Please pick a star rating.'); return; }
    setError(''); setSubmitting(true);
    try {
      await addDoc(colRef(), {
        uid:         user.uid,
        displayName: user.displayName || user.email.split('@')[0],
        photoURL:    user.photoURL || '',
        text:        text.trim(),
        stars,
        createdAt:   serverTimestamp(),
      });
      setText(''); setStars(0);
    } catch { setError('Failed to post. Try again.'); }
    finally { setSubmitting(false); }
  };

  const deleteReview = (id) => deleteDoc(doc(db, 'movies', String(movieId), 'reviews', id));

  return (
    <section>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 rounded-full" style={{ background: 'var(--red)' }} />
          <h3 className="font-display text-2xl tracking-wide" style={{ color: 'var(--text)' }}>Reviews</h3>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-accent"
            style={{ background: 'rgba(229,9,20,0.15)', color: 'var(--red)', border: '1px solid rgba(229,9,20,0.25)' }}>
            {reviews.length}
          </span>
        </div>
        {avgRating && (
          <div className="flex items-center gap-2">
            <FiStar size={15} fill="var(--gold)" color="var(--gold)" />
            <span className="font-accent text-lg font-bold" style={{ color: 'var(--gold)' }}>{avgRating}</span>
            <span className="text-xs" style={{ color: 'var(--muted)' }}>avg</span>
          </div>
        )}
      </div>

      {/* Write review box */}
      {user ? (
        <motion.form onSubmit={submit} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-5 mb-7"
          style={{ background: 'rgba(17,17,24,0.75)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-3 mb-4">
            {user.photoURL
              ? <img src={user.photoURL} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
              : <div className="w-9 h-9 rounded-full flex items-center justify-center font-display text-sm flex-shrink-0"
                  style={{ background: 'var(--red)', color: '#fff' }}>
                  {(user.displayName || user.email)[0].toUpperCase()}
                </div>
            }
            <div>
              <p className="text-sm font-semibold font-body leading-none mb-1" style={{ color: 'var(--text)' }}>
                {user.displayName || user.email.split('@')[0]}
              </p>
              <StarPicker value={stars} onChange={setStars} />
            </div>
          </div>

          <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
            placeholder="Share your thoughts on this film…"
            className="w-full rounded-xl px-4 py-3 text-sm font-body outline-none resize-none mb-3 transition-all"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', color: 'var(--text)' }}
            onFocus={e => e.target.style.borderColor = 'rgba(229,9,20,0.5)'}
            onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.07)'} />

          <AnimatePresence>
            {error && (
              <motion.p initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="text-xs mb-3 font-body px-3 py-2 rounded-lg"
                style={{ color: '#ff6b6b', background: 'rgba(229,9,20,0.1)' }}>
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          <div className="flex justify-end">
            <motion.button type="submit" disabled={submitting}
              whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-body font-semibold"
              style={{ background: 'var(--red)', color: '#fff', opacity: submitting ? 0.6 : 1, boxShadow: '0 0 20px rgba(229,9,20,0.25)' }}>
              <FiSend size={14} /> {submitting ? 'Posting…' : 'Post Review'}
            </motion.button>
          </div>
        </motion.form>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="rounded-2xl p-5 mb-7 flex items-center justify-between flex-wrap gap-4"
          style={{ background: 'rgba(229,9,20,0.06)', border: '1px dashed rgba(229,9,20,0.28)' }}>
          <div className="flex items-center gap-3">
            <FiMessageSquare size={18} style={{ color: 'var(--red)' }} />
            <p className="text-sm font-body" style={{ color: 'var(--muted)' }}>Sign in to leave a review</p>
          </div>
          <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
            onClick={() => navigate('/auth')}
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-body font-medium"
            style={{ background: 'var(--red)', color: '#fff' }}>
            <FiLogIn size={14} /> Sign In
          </motion.button>
        </motion.div>
      )}

      {/* Review list */}
      <div className="flex flex-col gap-4">
        <AnimatePresence>
          {reviews.map((r, i) => (
            <motion.div key={r.id} layout
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }} transition={{ delay: i * 0.04 }}
              className="rounded-2xl p-5 group"
              style={{ background: 'rgba(17,17,24,0.6)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  {r.photoURL
                    ? <img src={r.photoURL} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                    : <div className="w-9 h-9 rounded-full flex items-center justify-center font-display text-sm flex-shrink-0"
                        style={{ background: 'rgba(229,9,20,0.22)', color: 'var(--red)' }}>
                        {r.displayName?.[0]?.toUpperCase() || '?'}
                      </div>
                  }
                  <div>
                    <p className="text-sm font-semibold font-body leading-none" style={{ color: 'var(--text)' }}>{r.displayName}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex">
                        {[1,2,3,4,5].map(n => (
                          <FiStar key={n} size={11}
                            fill={n <= r.stars ? 'var(--gold)' : 'none'}
                            color={n <= r.stars ? 'var(--gold)' : 'var(--border)'} />
                        ))}
                      </div>
                      <span className="text-xs font-accent" style={{ color: 'var(--muted)' }}>{timeAgo(r.createdAt)}</span>
                    </div>
                  </div>
                </div>
                {(user?.uid === r.uid || profile?.isAdmin) && (
                  <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    onClick={() => deleteReview(r.id)}
                    className="opacity-0 group-hover:opacity-100 transition-all w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ color: 'var(--muted)', background: 'rgba(255,255,255,0.04)' }}>
                    <FiTrash2 size={13} />
                  </motion.button>
                )}
              </div>
              <p className="mt-3 text-sm font-body leading-relaxed" style={{ color: 'rgba(232,232,240,0.78)' }}>{r.text}</p>
            </motion.div>
          ))}
        </AnimatePresence>
        {reviews.length === 0 && (
          <div className="text-center py-10">
            <FiMessageSquare size={34} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
            <p className="text-sm font-body" style={{ color: 'var(--muted)' }}>No reviews yet — be the first!</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ReviewSection;
